import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Loader2, Download, Printer, Save, User, Phone, Mail, MapPin, Briefcase, Heart, Shield, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import API from '@/config/api';
// API imported from config;

const DCFEnrollmentForm = ({ open, onClose, childId, childName, token }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('child');
  
  const [formData, setFormData] = useState({
    child_last_name: '',
    child_first_name: '',
    child_middle_initial: '',
    child_birthdate: '',
    first_day_attendance: '',
    parent_guardian_a: {
      name: '',
      relationship: '',
      home_cell_phone: '',
      email: '',
      home_address: '',
      city: '',
      state: 'WI',
      zip_code: '',
      child_resides_here: null,
      employer: '',
      work_phone: ''
    },
    parent_guardian_b: {
      name: '',
      relationship: '',
      home_cell_phone: '',
      email: '',
      home_address: '',
      city: '',
      state: 'WI',
      zip_code: '',
      child_resides_here: null,
      employer: '',
      work_phone: ''
    },
    authorized_person_a: {
      name: '',
      relationship: '',
      home_cell_phone: '',
      email: '',
      employer: '',
      work_phone: ''
    },
    authorized_person_b: {
      name: '',
      relationship: '',
      home_cell_phone: '',
      email: '',
      employer: '',
      work_phone: ''
    },
    emergency_contact: {
      name: '',
      relationship: '',
      home_cell_phone: '',
      email: '',
      employer: '',
      work_phone: '',
      authorized_pickup: null
    },
    physician: {
      name: '',
      address: '',
      city: '',
      state: 'WI',
      zip_code: '',
      phone: ''
    },
    auth_emergency_medical: null,
    auth_field_trips_transported: null,
    auth_field_trips_walking: null,
    auth_reviewed_policies: null,
    auth_informed_of_pets: null,
    parent_signature: '',
    signature_date: ''
  });

  useEffect(() => {
    if (open && childId) {
      loadData();
    }
  }, [open, childId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/children/${childId}/dcf-enrollment-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          ...response.data,
          parent_guardian_a: { ...prev.parent_guardian_a, ...response.data.parent_guardian_a },
          parent_guardian_b: { ...prev.parent_guardian_b, ...response.data.parent_guardian_b },
          authorized_person_a: { ...prev.authorized_person_a, ...response.data.authorized_person_a },
          authorized_person_b: { ...prev.authorized_person_b, ...response.data.authorized_person_b },
          emergency_contact: { ...prev.emergency_contact, ...response.data.emergency_contact },
          physician: { ...prev.physician, ...response.data.physician }
        }));
      }
    } catch (error) {
      console.error('Error loading enrollment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/children/${childId}/dcf-enrollment-data`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Enrollment form saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save enrollment form');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/forms/dcf-0062`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DCF-F-CFS0062_Enrollment_${childName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handlePrint = async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/forms/dcf-0062`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
    } catch (error) {
      toast.error('Failed to open for printing');
    }
  };

  const updateNestedField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center p-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2">Loading enrollment form...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dcf-enrollment-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5 text-primary" />
            DCF Child Care Enrollment Form (DCF-F-CFS0062)
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Official Wisconsin DCF enrollment form - {childName}
          </p>
        </DialogHeader>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-enrollment-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Form
          </Button>
          <Button variant="outline" onClick={handleDownload} className="gap-2" data-testid="download-enrollment-btn">
            <Download className="w-4 h-4" />
            Download PDF
          </Button>
          <Button variant="outline" onClick={handlePrint} className="gap-2">
            <Printer className="w-4 h-4" />
            Print
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="child" data-testid="tab-child">Child</TabsTrigger>
            <TabsTrigger value="parents" data-testid="tab-parents">Parents</TabsTrigger>
            <TabsTrigger value="authorized" data-testid="tab-authorized">Authorized</TabsTrigger>
            <TabsTrigger value="medical" data-testid="tab-medical">Medical</TabsTrigger>
            <TabsTrigger value="authorize" data-testid="tab-authorize">Authorizations</TabsTrigger>
          </TabsList>

          {/* CHILD INFORMATION */}
          <TabsContent value="child" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />
                  Child Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input
                      value={formData.child_last_name}
                      onChange={(e) => setFormData({ ...formData, child_last_name: e.target.value })}
                      placeholder="Smith"
                      data-testid="child-last-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input
                      value={formData.child_first_name}
                      onChange={(e) => setFormData({ ...formData, child_first_name: e.target.value })}
                      placeholder="Emma"
                      data-testid="child-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Middle Initial</Label>
                    <Input
                      value={formData.child_middle_initial}
                      onChange={(e) => setFormData({ ...formData, child_middle_initial: e.target.value })}
                      placeholder="J"
                      maxLength={1}
                      className="w-16"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Birthdate (mm/dd/yyyy) *</Label>
                    <Input
                      type="date"
                      value={formData.child_birthdate}
                      onChange={(e) => setFormData({ ...formData, child_birthdate: e.target.value })}
                      data-testid="child-birthdate"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>First Day of Attendance</Label>
                    <Input
                      type="date"
                      value={formData.first_day_attendance}
                      onChange={(e) => setFormData({ ...formData, first_day_attendance: e.target.value })}
                      data-testid="first-day-attendance"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PARENT/GUARDIAN INFORMATION */}
          <TabsContent value="parents" className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>Note:</strong> All parents / guardians are permitted to visit during center hours and are 
              allowed to pick up the child unless access is prohibited or restricted by a court order.
            </div>

            {/* Parent/Guardian A */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />
                  Parent / Guardian A (Primary)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input
                      value={formData.parent_guardian_a.name}
                      onChange={(e) => updateNestedField('parent_guardian_a', 'name', e.target.value)}
                      placeholder="John Smith"
                      data-testid="parent-a-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Relationship to Child *</Label>
                    <Input
                      value={formData.parent_guardian_a.relationship}
                      onChange={(e) => updateNestedField('parent_guardian_a', 'relationship', e.target.value)}
                      placeholder="Father"
                      data-testid="parent-a-relationship"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Phone className="w-3 h-3" /> Home / Cell Phone *</Label>
                    <Input
                      value={formData.parent_guardian_a.home_cell_phone}
                      onChange={(e) => updateNestedField('parent_guardian_a', 'home_cell_phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
                    <Input
                      type="email"
                      value={formData.parent_guardian_a.email}
                      onChange={(e) => updateNestedField('parent_guardian_a', 'email', e.target.value)}
                      placeholder="parent@email.com"
                    />
                  </div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Home Address</Label>
                  <Input
                    value={formData.parent_guardian_a.home_address}
                    onChange={(e) => updateNestedField('parent_guardian_a', 'home_address', e.target.value)}
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.parent_guardian_a.city}
                      onChange={(e) => updateNestedField('parent_guardian_a', 'city', e.target.value)}
                      placeholder="Madison"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.parent_guardian_a.state}
                      onChange={(e) => updateNestedField('parent_guardian_a', 'state', e.target.value)}
                      placeholder="WI"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Zip Code</Label>
                    <Input
                      value={formData.parent_guardian_a.zip_code}
                      onChange={(e) => updateNestedField('parent_guardian_a', 'zip_code', e.target.value)}
                      placeholder="53703"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Does child reside at this location?</Label>
                  <RadioGroup
                    value={formData.parent_guardian_a.child_resides_here === true ? 'yes' : formData.parent_guardian_a.child_resides_here === false ? 'no' : ''}
                    onValueChange={(v) => updateNestedField('parent_guardian_a', 'child_resides_here', v === 'yes')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="pa-resides-yes" />
                      <Label htmlFor="pa-resides-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="pa-resides-no" />
                      <Label htmlFor="pa-resides-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> Place of Employment</Label>
                    <Input
                      value={formData.parent_guardian_a.employer}
                      onChange={(e) => updateNestedField('parent_guardian_a', 'employer', e.target.value)}
                      placeholder="ABC Company"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Work Phone</Label>
                    <Input
                      value={formData.parent_guardian_a.work_phone}
                      onChange={(e) => updateNestedField('parent_guardian_a', 'work_phone', e.target.value)}
                      placeholder="(555) 987-6543"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Parent/Guardian B */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  Parent / Guardian B (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input
                      value={formData.parent_guardian_b.name}
                      onChange={(e) => updateNestedField('parent_guardian_b', 'name', e.target.value)}
                      placeholder="Jane Smith"
                      data-testid="parent-b-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Relationship to Child</Label>
                    <Input
                      value={formData.parent_guardian_b.relationship}
                      onChange={(e) => updateNestedField('parent_guardian_b', 'relationship', e.target.value)}
                      placeholder="Mother"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Phone className="w-3 h-3" /> Home / Cell Phone</Label>
                    <Input
                      value={formData.parent_guardian_b.home_cell_phone}
                      onChange={(e) => updateNestedField('parent_guardian_b', 'home_cell_phone', e.target.value)}
                      placeholder="(555) 234-5678"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
                    <Input
                      type="email"
                      value={formData.parent_guardian_b.email}
                      onChange={(e) => updateNestedField('parent_guardian_b', 'email', e.target.value)}
                      placeholder="parent2@email.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Home Address</Label>
                  <Input
                    value={formData.parent_guardian_b.home_address}
                    onChange={(e) => updateNestedField('parent_guardian_b', 'home_address', e.target.value)}
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.parent_guardian_b.city}
                      onChange={(e) => updateNestedField('parent_guardian_b', 'city', e.target.value)}
                      placeholder="Madison"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.parent_guardian_b.state}
                      onChange={(e) => updateNestedField('parent_guardian_b', 'state', e.target.value)}
                      placeholder="WI"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Zip Code</Label>
                    <Input
                      value={formData.parent_guardian_b.zip_code}
                      onChange={(e) => updateNestedField('parent_guardian_b', 'zip_code', e.target.value)}
                      placeholder="53703"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Does child reside at this location?</Label>
                  <RadioGroup
                    value={formData.parent_guardian_b.child_resides_here === true ? 'yes' : formData.parent_guardian_b.child_resides_here === false ? 'no' : ''}
                    onValueChange={(v) => updateNestedField('parent_guardian_b', 'child_resides_here', v === 'yes')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="pb-resides-yes" />
                      <Label htmlFor="pb-resides-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="pb-resides-no" />
                      <Label htmlFor="pb-resides-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> Place of Employment</Label>
                    <Input
                      value={formData.parent_guardian_b.employer}
                      onChange={(e) => updateNestedField('parent_guardian_b', 'employer', e.target.value)}
                      placeholder="XYZ Corp"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Work Phone</Label>
                    <Input
                      value={formData.parent_guardian_b.work_phone}
                      onChange={(e) => updateNestedField('parent_guardian_b', 'work_phone', e.target.value)}
                      placeholder="(555) 876-5432"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AUTHORIZED PERSONS */}
          <TabsContent value="authorized" className="space-y-4 mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Authorized Persons:</strong> Persons other than parents / guardians who are authorized to 
              pick up the child or accept the child if dropped off. If no one, write "None."
            </div>

            {/* Authorized Person A */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  Authorized Person A
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name (or "None")</Label>
                    <Input
                      value={formData.authorized_person_a.name}
                      onChange={(e) => updateNestedField('authorized_person_a', 'name', e.target.value)}
                      placeholder="Grandma Mary or None"
                      data-testid="authorized-a-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Relationship to Child</Label>
                    <Input
                      value={formData.authorized_person_a.relationship}
                      onChange={(e) => updateNestedField('authorized_person_a', 'relationship', e.target.value)}
                      placeholder="Grandmother"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Phone className="w-3 h-3" /> Home / Cell Phone</Label>
                    <Input
                      value={formData.authorized_person_a.home_cell_phone}
                      onChange={(e) => updateNestedField('authorized_person_a', 'home_cell_phone', e.target.value)}
                      placeholder="(555) 345-6789"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
                    <Input
                      type="email"
                      value={formData.authorized_person_a.email}
                      onChange={(e) => updateNestedField('authorized_person_a', 'email', e.target.value)}
                      placeholder="grandma@email.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> Place of Employment</Label>
                    <Input
                      value={formData.authorized_person_a.employer}
                      onChange={(e) => updateNestedField('authorized_person_a', 'employer', e.target.value)}
                      placeholder="Retired"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Work Phone</Label>
                    <Input
                      value={formData.authorized_person_a.work_phone}
                      onChange={(e) => updateNestedField('authorized_person_a', 'work_phone', e.target.value)}
                      placeholder="N/A"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Authorized Person B */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-4 h-4 text-muted-foreground" />
                  Authorized Person B (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={formData.authorized_person_b.name}
                      onChange={(e) => updateNestedField('authorized_person_b', 'name', e.target.value)}
                      placeholder="Aunt Sarah"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Relationship to Child</Label>
                    <Input
                      value={formData.authorized_person_b.relationship}
                      onChange={(e) => updateNestedField('authorized_person_b', 'relationship', e.target.value)}
                      placeholder="Aunt"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Phone className="w-3 h-3" /> Home / Cell Phone</Label>
                    <Input
                      value={formData.authorized_person_b.home_cell_phone}
                      onChange={(e) => updateNestedField('authorized_person_b', 'home_cell_phone', e.target.value)}
                      placeholder="(555) 456-7890"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
                    <Input
                      type="email"
                      value={formData.authorized_person_b.email}
                      onChange={(e) => updateNestedField('authorized_person_b', 'email', e.target.value)}
                      placeholder="aunt@email.com"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Emergency Contact */}
            <Card className="border-red-200">
              <CardHeader className="pb-2 bg-red-50 rounded-t-lg">
                <CardTitle className="text-base flex items-center gap-2 text-red-800">
                  <Heart className="w-4 h-4" />
                  Emergency Contact
                </CardTitle>
                <p className="text-xs text-red-600">
                  Person to be notified in an emergency when parents / guardians cannot be reached.
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={formData.emergency_contact.name}
                      onChange={(e) => updateNestedField('emergency_contact', 'name', e.target.value)}
                      placeholder="Uncle Bob"
                      data-testid="emergency-contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Relationship to Child *</Label>
                    <Input
                      value={formData.emergency_contact.relationship}
                      onChange={(e) => updateNestedField('emergency_contact', 'relationship', e.target.value)}
                      placeholder="Uncle"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Phone className="w-3 h-3" /> Home / Cell Phone *</Label>
                    <Input
                      value={formData.emergency_contact.home_cell_phone}
                      onChange={(e) => updateNestedField('emergency_contact', 'home_cell_phone', e.target.value)}
                      placeholder="(555) 567-8901"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
                    <Input
                      type="email"
                      value={formData.emergency_contact.email}
                      onChange={(e) => updateNestedField('emergency_contact', 'email', e.target.value)}
                      placeholder="uncle@email.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> Place of Employment</Label>
                    <Input
                      value={formData.emergency_contact.employer}
                      onChange={(e) => updateNestedField('emergency_contact', 'employer', e.target.value)}
                      placeholder="Local Business"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Work Phone</Label>
                    <Input
                      value={formData.emergency_contact.work_phone}
                      onChange={(e) => updateNestedField('emergency_contact', 'work_phone', e.target.value)}
                      placeholder="(555) 678-9012"
                    />
                  </div>
                </div>
                <div className="space-y-2 pt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Label className="font-medium text-green-800">Is this person authorized to pick up the child?</Label>
                  <RadioGroup
                    value={formData.emergency_contact.authorized_pickup === true ? 'yes' : formData.emergency_contact.authorized_pickup === false ? 'no' : ''}
                    onValueChange={(v) => updateNestedField('emergency_contact', 'authorized_pickup', v === 'yes')}
                    className="flex gap-4"
                    data-testid="ec-authorized-pickup"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="ec-pickup-yes" />
                      <Label htmlFor="ec-pickup-yes" className="cursor-pointer">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="ec-pickup-no" />
                      <Label htmlFor="ec-pickup-no" className="cursor-pointer">No</Label>
                    </div>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MEDICAL INFORMATION */}
          <TabsContent value="medical" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Heart className="w-4 h-4 text-red-500" />
                  Physician or Medical Facility
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Physician / Facility Name *</Label>
                    <Input
                      value={formData.physician.name}
                      onChange={(e) => updateNestedField('physician', 'name', e.target.value)}
                      placeholder="Dr. Johnson / Madison Pediatrics"
                      data-testid="physician-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Phone className="w-3 h-3" /> Telephone Number *</Label>
                    <Input
                      value={formData.physician.phone}
                      onChange={(e) => updateNestedField('physician', 'phone', e.target.value)}
                      placeholder="(555) 789-0123"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</Label>
                  <Input
                    value={formData.physician.address}
                    onChange={(e) => updateNestedField('physician', 'address', e.target.value)}
                    placeholder="456 Medical Center Dr"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.physician.city}
                      onChange={(e) => updateNestedField('physician', 'city', e.target.value)}
                      placeholder="Madison"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.physician.state}
                      onChange={(e) => updateNestedField('physician', 'state', e.target.value)}
                      placeholder="WI"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Zip Code</Label>
                    <Input
                      value={formData.physician.zip_code}
                      onChange={(e) => updateNestedField('physician', 'zip_code', e.target.value)}
                      placeholder="53705"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AUTHORIZATIONS */}
          <TabsContent value="authorize" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  Authorizations
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Please indicate Yes or No for each authorization below.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Authorization 1: Emergency Medical */}
                <div className="p-4 border rounded-lg bg-gray-50">
                  <div className="flex items-start gap-4">
                    <RadioGroup
                      value={formData.auth_emergency_medical === true ? 'yes' : formData.auth_emergency_medical === false ? 'no' : ''}
                      onValueChange={(v) => setFormData({ ...formData, auth_emergency_medical: v === 'yes' })}
                      className="flex gap-4 shrink-0"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="yes" id="auth1-yes" />
                        <Label htmlFor="auth1-yes">Yes</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="auth1-no" />
                        <Label htmlFor="auth1-no">No</Label>
                      </div>
                    </RadioGroup>
                    <p className="text-sm">
                      I hereby give my consent for <strong>emergency medical care or treatment</strong> to be 
                      used only if I cannot be reached immediately.
                    </p>
                  </div>
                </div>

                {/* Authorization 2: Field Trips */}
                <div className="p-4 border rounded-lg bg-gray-50">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0">
                      <RadioGroup
                        value={(formData.auth_field_trips_transported || formData.auth_field_trips_walking) ? 'yes' : formData.auth_field_trips_transported === false && formData.auth_field_trips_walking === false ? 'no' : ''}
                        onValueChange={(v) => {
                          if (v === 'no') {
                            setFormData({ ...formData, auth_field_trips_transported: false, auth_field_trips_walking: false });
                          }
                        }}
                        className="flex gap-4"
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="yes" id="auth2-yes" />
                          <Label htmlFor="auth2-yes">Yes</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="no" id="auth2-no" />
                          <Label htmlFor="auth2-no">No</Label>
                        </div>
                      </RadioGroup>
                    </div>
                    <div className="text-sm">
                      <p className="mb-2">I give permission for my child to participate in:</p>
                      <div className="flex flex-wrap gap-4 ml-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.auth_field_trips_transported || false}
                            onChange={(e) => setFormData({ ...formData, auth_field_trips_transported: e.target.checked })}
                            className="rounded border-gray-300"
                          />
                          <span>Transported field trips</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.auth_field_trips_walking || false}
                            onChange={(e) => setFormData({ ...formData, auth_field_trips_walking: e.target.checked })}
                            className="rounded border-gray-300"
                          />
                          <span>Walking field trips and other activities</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Authorization 3: Reviewed Policies */}
                <div className="p-4 border rounded-lg bg-gray-50">
                  <div className="flex items-start gap-4">
                    <RadioGroup
                      value={formData.auth_reviewed_policies === true ? 'yes' : formData.auth_reviewed_policies === false ? 'no' : ''}
                      onValueChange={(v) => setFormData({ ...formData, auth_reviewed_policies: v === 'yes' })}
                      className="flex gap-4 shrink-0"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="yes" id="auth3-yes" />
                        <Label htmlFor="auth3-yes">Yes</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="auth3-no" />
                        <Label htmlFor="auth3-no">No</Label>
                      </div>
                    </RadioGroup>
                    <p className="text-sm">
                      I have had an opportunity to review the <strong>policies of this child care center</strong> and 
                      a summary of the Wisconsin Rules for Licensing Child Care Centers.
                    </p>
                  </div>
                </div>

                {/* Authorization 4: Informed of Pets */}
                <div className="p-4 border rounded-lg bg-gray-50">
                  <div className="flex items-start gap-4">
                    <RadioGroup
                      value={formData.auth_informed_of_pets === true ? 'yes' : formData.auth_informed_of_pets === false ? 'no' : ''}
                      onValueChange={(v) => setFormData({ ...formData, auth_informed_of_pets: v === 'yes' })}
                      className="flex gap-4 shrink-0"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="yes" id="auth4-yes" />
                        <Label htmlFor="auth4-yes">Yes</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="auth4-no" />
                        <Label htmlFor="auth4-no">No</Label>
                      </div>
                    </RadioGroup>
                    <p className="text-sm">
                      I have been informed of the <strong>number of pets</strong> in the center and their degree 
                      of contact with the enrolled children.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Signature Section */}
            <Card className="border-primary">
              <CardHeader className="pb-2 bg-primary/5 rounded-t-lg">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  Parent / Guardian Signature
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Signature (Type Full Name) *</Label>
                    <Input
                      value={formData.parent_signature}
                      onChange={(e) => setFormData({ ...formData, parent_signature: e.target.value })}
                      placeholder="John Smith"
                      className="font-cursive text-lg"
                      data-testid="parent-signature"
                    />
                    <p className="text-xs text-muted-foreground">
                      By typing your name, you acknowledge this as your electronic signature.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Date Signed *</Label>
                    <Input
                      type="date"
                      value={formData.signature_date}
                      onChange={(e) => setFormData({ ...formData, signature_date: e.target.value })}
                      data-testid="signature-date"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-enrollment-final-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Enrollment Form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DCFEnrollmentForm;
